/* eslint-disable @typescript-eslint/no-explicit-any */
import type { SupabaseClient } from "@supabase/supabase-js";
import type { Database, Json } from "../../types/database";
import type {
  Conversation,
  ConversationMessage,
  Decision,
  ExecutionPlan,
  ExecutionRecord,
  Goal,
  Reflection,
} from "./types";
import type {
  ApprovalEvent,
  ApprovalRequest,
  ContextSource,
  ProductContext,
  SafeReasoningSummary,
  TimelineEvent,
  ToolIntent,
} from "./liveTypes";
import type { CyanIntelligenceRepository } from "./repositories";

type ListFilter = {
  organizationId?: string;
  productId?: string;
  conversationId?: string;
  status?: string;
  planId?: string;
  limit?: number;
};

function json(value: unknown): Json {
  return value as Json;
}
function rowError(error: unknown, table: string): void {
  if (error) throw new Error(`Cyan repository ${table} persistence failed.`);
}

export class SupabaseCyanIntelligenceRepository implements CyanIntelligenceRepository {
  private readonly db: any;
  constructor(supabase: SupabaseClient<Database>) {
    this.db = supabase as any;
  }

  private query(table: string) {
    return this.db.from(table);
  }
  async resolveOrganizationId(id: string): Promise<string> {
    if (!id.startsWith("org_")) return id;
    const slug = id.replace(/^org_/, "");
    const { data, error } = await this.query("organizations")
      .select("id")
      .eq("slug", slug)
      .single();
    rowError(error, "organizations");
    return data.id;
  }
  private async resolveProductId(id?: string | null): Promise<string | null> {
    if (!id) return null;
    if (!id.startsWith("product_")) return id;
    const slug = id.replace(/^product_/, "").replaceAll("_", "-");
    const { data, error } = await this.query("products")
      .select("id")
      .eq("slug", slug)
      .single();
    rowError(error, "products");
    return data.id;
  }
  private async resolveScope(input: {
    organizationId: string;
    productId?: string | null;
  }): Promise<{ organizationId: string; productId: string | null }> {
    return {
      organizationId: await this.resolveOrganizationId(input.organizationId),
      productId: await this.resolveProductId(input.productId),
    };
  }
  private async applyFilter(query: any, filter?: ListFilter) {
    let q = query;
    const organizationId = filter?.organizationId
      ? await this.resolveOrganizationId(filter.organizationId)
      : undefined;
    const productId = filter?.productId
      ? await this.resolveProductId(filter.productId)
      : undefined;
    if (organizationId) q = q.eq("organization_id", organizationId);
    if (productId) q = q.eq("product_id", productId);
    if (filter?.conversationId)
      q = q.eq("conversation_id", filter.conversationId);
    if (filter?.status) q = q.eq("status", filter.status);
    return q.limit(filter?.limit ?? 50);
  }
  private async conversationScope(
    conversationId: string,
  ): Promise<{ organizationId: string; productId: string | null }> {
    const { data, error } = await this.query("cyan_conversations")
      .select("organization_id, product_id")
      .eq("id", conversationId)
      .single();
    rowError(error, "cyan_conversations");
    return {
      organizationId: data?.organization_id ?? "org_francacyan",
      productId: data?.product_id ?? null,
    };
  }
  private async approvalScope(
    approvalId: string,
  ): Promise<{ organizationId: string; productId: string | null }> {
    const { data, error } = await this.query("cyan_approvals")
      .select("organization_id, product_id")
      .eq("id", approvalId)
      .single();
    rowError(error, "cyan_approvals");
    return {
      organizationId: data?.organization_id ?? "org_francacyan",
      productId: data?.product_id ?? null,
    };
  }

  async saveGoal(goal: Goal) {
    const scope = await this.resolveScope(goal);
    const { error } = await this.query("cyan_goals").upsert({
      id: goal.id,
      organization_id: scope.organizationId,
      product_id: scope.productId,
      department_id: goal.departmentId ?? null,
      scope: goal.scope,
      title: goal.title,
      description: goal.description,
      status: goal.status,
      milestones: json(goal.milestones),
      completion_percent: goal.completionPercent,
      updated_at: goal.updatedAt,
    });
    rowError(error, "cyan_goals");
  }
  async savePlan(plan: ExecutionPlan) {
    const scope = await this.resolveScope(plan);
    const { error } = await this.query("cyan_plans").upsert({
      id: plan.id,
      goal_id: plan.goalId,
      organization_id: scope.organizationId,
      product_id: scope.productId,
      summary: plan.summary,
      objectives: json(plan.objectives),
      status: plan.status,
      total_estimated_effort_hours: plan.totalEstimatedEffortHours,
    });
    rowError(error, "cyan_plans");
  }
  async saveDecision(decision: Decision) {
    const organizationId = await this.resolveOrganizationId(
      decision.organizationId,
    );
    const { error } = await this.query("cyan_decisions").upsert({
      id: decision.id,
      organization_id: organizationId,
      question: decision.question,
      selected_option: json(decision.selectedOption),
      score: decision.score,
      explanation: decision.explanation,
      options: json(decision.options),
    });
    rowError(error, "cyan_decisions");
  }
  async saveReflection(reflection: Reflection) {
    const scope = await this.resolveScope(reflection);
    const { error } = await this.query("cyan_reflections").insert({
      id: reflection.id,
      task_id: reflection.taskId,
      organization_id: scope.organizationId,
      product_id: scope.productId,
      happened: reflection.happened,
      succeeded: json(reflection.succeeded),
      failed: json(reflection.failed),
      improvements: json(reflection.improvements),
      update_knowledge: reflection.updateKnowledge,
      update_memory: reflection.updateMemory,
      update_planning: reflection.updatePlanning,
    });
    rowError(error, "cyan_reflections");
  }
  async saveConversation(conversation: Conversation) {
    const scope = await this.resolveScope(conversation);
    const { error } = await this.query("cyan_conversations").upsert({
      id: conversation.id,
      organization_id: scope.organizationId,
      product_id: scope.productId,
      title: conversation.title,
      context: json(conversation.context),
      messages: json(conversation.messages),
      updated_at: conversation.updatedAt,
    });
    rowError(error, "cyan_conversations");
  }
  async saveMessage(conversationId: string, message: ConversationMessage) {
    const scope = await this.conversationScope(conversationId);
    const { error } = await this.query("cyan_messages").insert({
      id: message.id,
      conversation_id: conversationId,
      organization_id: scope.organizationId,
      product_id: scope.productId,
      role: message.role,
      content: message.content,
      intent: message.intent,
      safe_reasoning: json(message.reasoning),
      memory_references: json(message.memoryReferences),
      knowledge_references: json(message.knowledgeReferences),
      tool_usage: json(message.toolUsage),
      decision_references: json(message.decisionReferences),
      status: "sent",
      correlation_id: message.id,
    });
    rowError(error, "cyan_messages");
  }
  async saveExecution(record: ExecutionRecord) {
    const organizationId = await this.resolveOrganizationId(
      record.organizationId,
    );
    const { error } = await this.query("cyan_executions").upsert({
      id: record.id,
      organization_id: organizationId,
      plan_id: record.planId,
      status: record.status,
      created_task_ids: json(record.createdTaskIds),
      knowledge_entry_ids: json(record.knowledgeEntryIds),
      reflection_ids: json(record.reflectionIds),
    });
    rowError(error, "cyan_executions");
  }
  async saveReasoningSummary(summary: SafeReasoningSummary) {
    const scope = await this.resolveScope(summary);
    const { error } = await this.query("cyan_reasoning_summaries").insert({
      id: summary.id,
      organization_id: scope.organizationId,
      product_id: scope.productId,
      conversation_id: summary.conversationId,
      interpreted_intent: summary.interpretedIntent,
      known_facts_used: json(summary.knownFactsUsed),
      dependencies_identified: json(summary.dependenciesIdentified),
      assumptions: json(summary.assumptions),
      questions: json(summary.questions),
      options_considered: json(summary.optionsConsidered),
      selected_approach: summary.selectedApproach,
      confidence: summary.confidence,
      risk: summary.risk,
      evidence_references: json(summary.evidenceReferences),
      status: "current",
      correlation_id: summary.id,
      metadata: {},
    });
    rowError(error, "cyan_reasoning_summaries");
  }
  async saveApproval(approval: ApprovalRequest) {
    const scope = await this.resolveScope(approval);
    const { error } = await this.query("cyan_approvals").upsert({
      id: approval.id,
      organization_id: scope.organizationId,
      product_id: scope.productId,
      conversation_id: approval.conversationId,
      plan_id: approval.planId ?? null,
      category: approval.category,
      title: approval.title,
      summary: approval.summary,
      status: approval.status,
      requested_by: approval.requestedBy,
      decided_by: approval.decidedBy ?? null,
      decision_reason: approval.decisionReason ?? null,
      correlation_id: approval.id,
      metadata: {},
    });
    rowError(error, "cyan_approvals");
  }
  async saveApprovalEvent(event: ApprovalEvent) {
    const scope = await this.approvalScope(event.approvalId);
    const { error } = await this.query("cyan_approval_events").insert({
      id: event.id,
      approval_id: event.approvalId,
      organization_id: scope.organizationId,
      product_id: scope.productId,
      action: event.action,
      actor_user_id: null,
      actor_email: event.actor,
      reason: event.reason,
      correlation_id: event.id,
      metadata: {},
    });
    rowError(error, "cyan_approval_events");
  }
  async saveContextSelection(
    context: ProductContext & {
      conversationId?: string;
      correlationId: string;
      owner: string;
    },
  ) {
    const scope = await this.resolveScope(context);
    const { error } = await this.query("cyan_context_selections").insert({
      organization_id: scope.organizationId,
      product_id: scope.productId,
      project_id: context.projectId ?? null,
      conversation_id: context.conversationId ?? null,
      owner_user_id: null,
      owner_email: context.owner,
      correlation_id: context.correlationId,
      status: "active",
      metadata: { productName: context.productName },
    });
    rowError(error, "cyan_context_selections");
  }
  async saveToolIntent(intent: ToolIntent) {
    const scope = await this.resolveScope(intent);
    const { error } = await this.query("cyan_tool_intents").upsert({
      id: intent.id,
      organization_id: scope.organizationId,
      product_id: scope.productId,
      plan_id: intent.planId,
      tool: intent.tool,
      purpose: intent.purpose,
      requested_operation: intent.requestedOperation,
      environment: intent.environment,
      permissions_needed: json(intent.permissionsNeeded),
      risk: intent.risk,
      cost_estimate: intent.costEstimate,
      approval_required: intent.approvalRequired,
      connection_status: intent.connectionStatus,
      status: intent.status,
      blocked_reason: intent.blockedReason ?? null,
      correlation_id: intent.id,
      metadata: {},
    });
    rowError(error, "cyan_tool_intents");
  }
  async saveTimelineEvent(event: TimelineEvent) {
    const scope = await this.resolveScope(event);
    const { error } = await this.query("cyan_timeline_events").insert({
      id: event.id,
      organization_id: scope.organizationId,
      product_id: scope.productId,
      conversation_id: event.conversationId ?? null,
      plan_id: event.planId ?? null,
      task_id: event.taskId ?? null,
      event_type: event.eventType,
      title: event.title,
      status: event.status,
      correlation_id: event.id,
      metadata: json(event.metadata),
    });
    rowError(error, "cyan_timeline_events");
  }
  async saveContextSource(source: ContextSource) {
    const { error } = await this.query("cyan_timeline_events").insert({
      organization_id: await this.resolveOrganizationId("org_francacyan"),
      product_id: await this.resolveProductId(source.productId),
      event_type: "context_source_selected",
      title: source.title,
      status: source.verificationStatus,
      correlation_id: source.sourceId,
      metadata: json(source),
    });
    rowError(error, "cyan_timeline_events");
  }
  async deleteConversation(conversationId: string): Promise<void> {
    const { error } = await this.db.rpc("owner_delete_cyan_conversation", {
      target_conversation_id: conversationId,
    });

    rowError(error, "owner_delete_cyan_conversation");
  }

  async listGoals(filter?: ListFilter): Promise<Goal[]> {
    const { data, error } = await this.applyFilter(
      this.query("cyan_goals")
        .select("*")
        .order("updated_at", { ascending: false }),
      filter,
    );
    rowError(error, "cyan_goals");
    return (data ?? []).map((r: any) => ({
      id: r.id,
      organizationId: r.organization_id,
      productId: r.product_id,
      departmentId: r.department_id,
      scope: r.scope,
      title: r.title,
      description: r.description,
      status: r.status,
      milestones: r.milestones ?? [],
      completionPercent: r.completion_percent,
      createdAt: r.created_at,
      updatedAt: r.updated_at,
    }));
  }
  async listPlans(filter?: ListFilter): Promise<ExecutionPlan[]> {
    const { data, error } = await this.applyFilter(
      this.query("cyan_plans")
        .select("*")
        .order("created_at", { ascending: false }),
      filter,
    );
    rowError(error, "cyan_plans");
    return (data ?? []).map((r: any) => ({
      id: r.id,
      goalId: r.goal_id,
      organizationId: r.organization_id,
      productId: r.product_id,
      summary: r.summary,
      objectives: r.objectives ?? [],
      status: r.status,
      totalEstimatedEffortHours: r.total_estimated_effort_hours,
      createdAt: r.created_at,
    }));
  }
  async listDecisions(filter?: ListFilter): Promise<Decision[]> {
    const { data, error } = await this.applyFilter(
      this.query("cyan_decisions")
        .select("*")
        .order("created_at", { ascending: false }),
      filter,
    );
    rowError(error, "cyan_decisions");
    return (data ?? []).map((r: any) => ({
      id: r.id,
      organizationId: r.organization_id,
      question: r.question,
      selectedOption: r.selected_option,
      score: r.score,
      explanation: r.explanation,
      options: r.options ?? [],
      createdAt: r.created_at,
    }));
  }
  async listReflections(filter?: ListFilter): Promise<Reflection[]> {
    const { data, error } = await this.applyFilter(
      this.query("cyan_reflections")
        .select("*")
        .order("created_at", { ascending: false }),
      filter,
    );
    rowError(error, "cyan_reflections");
    return (data ?? []).map((r: any) => ({
      id: r.id,
      taskId: r.task_id,
      organizationId: r.organization_id,
      productId: r.product_id,
      happened: r.happened,
      succeeded: r.succeeded ?? [],
      failed: r.failed ?? [],
      improvements: r.improvements ?? [],
      updateKnowledge: r.update_knowledge,
      updateMemory: r.update_memory,
      updatePlanning: r.update_planning,
      createdAt: r.created_at,
    }));
  }
  async listConversations(filter?: ListFilter): Promise<Conversation[]> {
    const { data, error } = await this.applyFilter(
      this.query("cyan_conversations")
        .select("*")
        .order("updated_at", { ascending: false }),
      filter,
    );
    rowError(error, "cyan_conversations");
    return (data ?? []).map((r: any) => ({
      id: r.id,
      organizationId: r.organization_id,
      productId: r.product_id,
      title: r.title,
      context: r.context ?? {},
      messages: r.messages ?? [],
      createdAt: r.created_at,
      updatedAt: r.updated_at,
    }));
  }
  async listMessages(conversationId: string): Promise<ConversationMessage[]> {
    const { data, error } = await this.query("cyan_messages")
      .select("*")
      .eq("conversation_id", conversationId)
      .order("created_at", { ascending: true })
      .limit(100);
    rowError(error, "cyan_messages");
    return (data ?? []).map((r: any) => ({
      id: r.id,
      role: r.role,
      content: r.content,
      intent: r.intent,
      reasoning: r.safe_reasoning ?? [],
      memoryReferences: r.memory_references ?? [],
      knowledgeReferences: r.knowledge_references ?? [],
      toolUsage: r.tool_usage ?? [],
      decisionReferences: r.decision_references ?? [],
      createdAt: r.created_at,
    }));
  }
  async listExecutions(filter?: ListFilter): Promise<ExecutionRecord[]> {
    const { data, error } = await this.applyFilter(
      this.query("cyan_executions")
        .select("*")
        .order("created_at", { ascending: false }),
      filter,
    );
    rowError(error, "cyan_executions");
    return (data ?? []).map((r: any) => ({
      id: r.id,
      organizationId: r.organization_id,
      planId: r.plan_id,
      status: r.status,
      createdTaskIds: r.created_task_ids ?? [],
      knowledgeEntryIds: r.knowledge_entry_ids ?? [],
      reflectionIds: r.reflection_ids ?? [],
      createdAt: r.created_at,
    }));
  }
  async listReasoningSummaries(
    filter?: ListFilter,
  ): Promise<SafeReasoningSummary[]> {
    const { data, error } = await this.applyFilter(
      this.query("cyan_reasoning_summaries")
        .select("*")
        .order("created_at", { ascending: false }),
      filter,
    );
    rowError(error, "cyan_reasoning_summaries");
    return (data ?? []).map((r: any) => ({
      id: r.id,
      organizationId: r.organization_id,
      productId: r.product_id,
      conversationId: r.conversation_id,
      interpretedIntent: r.interpreted_intent,
      knownFactsUsed: r.known_facts_used ?? [],
      dependenciesIdentified: r.dependencies_identified ?? [],
      assumptions: r.assumptions ?? [],
      questions: r.questions ?? [],
      optionsConsidered: r.options_considered ?? [],
      selectedApproach: r.selected_approach,
      confidence: r.confidence,
      risk: r.risk,
      evidenceReferences: r.evidence_references ?? [],
      createdAt: r.created_at,
    }));
  }
  async listApprovals(filter?: ListFilter): Promise<ApprovalRequest[]> {
    const { data, error } = await this.applyFilter(
      this.query("cyan_approvals")
        .select("*")
        .order("updated_at", { ascending: false }),
      filter,
    );
    rowError(error, "cyan_approvals");
    return (data ?? []).map((r: any) => ({
      id: r.id,
      organizationId: r.organization_id,
      productId: r.product_id,
      conversationId: r.conversation_id,
      planId: r.plan_id,
      category: r.category,
      title: r.title,
      summary: r.summary,
      status: r.status,
      requestedBy: r.requested_by,
      decidedBy: r.decided_by,
      decisionReason: r.decision_reason,
      createdAt: r.created_at,
      updatedAt: r.updated_at,
    }));
  }
  async listToolIntents(filter?: ListFilter): Promise<ToolIntent[]> {
    const { data, error } = await this.applyFilter(
      this.query("cyan_tool_intents")
        .select("*")
        .order("created_at", { ascending: false }),
      filter,
    );
    rowError(error, "cyan_tool_intents");
    return (data ?? []).map((r: any) => ({
      id: r.id,
      organizationId: r.organization_id,
      productId: r.product_id,
      planId: r.plan_id,
      tool: r.tool,
      purpose: r.purpose,
      requestedOperation: r.requested_operation,
      environment: r.environment,
      permissionsNeeded: r.permissions_needed ?? [],
      risk: r.risk,
      costEstimate: r.cost_estimate,
      approvalRequired: r.approval_required,
      connectionStatus: r.connection_status,
      status: r.status,
      blockedReason: r.blocked_reason,
    }));
  }
  async listTimeline(filter?: ListFilter): Promise<TimelineEvent[]> {
    const { data, error } = await this.applyFilter(
      this.query("cyan_timeline_events")
        .select("*")
        .order("created_at", { ascending: false }),
      filter,
    );
    rowError(error, "cyan_timeline_events");
    return (data ?? []).map((r: any) => ({
      id: r.id,
      organizationId: r.organization_id,
      productId: r.product_id,
      conversationId: r.conversation_id,
      planId: r.plan_id,
      taskId: r.task_id,
      eventType: r.event_type,
      title: r.title,
      status: r.status,
      metadata: r.metadata ?? {},
      createdAt: r.created_at,
    }));
  }
  async listContextSources(filter?: ListFilter): Promise<ContextSource[]> {
    const { data, error } = await this.applyFilter(
      this.query("cyan_timeline_events")
        .select("*")
        .eq("event_type", "context_source_selected")
        .order("created_at", { ascending: false }),
      filter,
    );
    rowError(error, "cyan_timeline_events");
    return (data ?? []).map((r: any) => r.metadata as ContextSource);
  }
}
